import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseService } from 'shared/services/BaseService';

interface EntityHistoryLedgerCardProps {
  /** The API service instance used to fetch history logs. */
  service: BaseService<any>;
  /** Column definitions configuration for the underlying DataGrid. */
  columns: GridColDef[];
  /** Additional query parameters or filters passed from the parent layout to filter the history. */
  outerParams: any;
  /** A mutable ref used as an imperative handle to programmatically trigger data reloads. */
  refreshTriggerRef: MutableRefObject<any>;
  /** Optional callback function executed when the user manually clicks the refresh button. */
  onManualRefresh?: () => void;
}

/**
 * EntityHistoryLedgerCard
 *
 * A business-focused card panel containing a data grid to display the operational history trail.
 * It provides users with a standardized interface to review tracking events, record modifications,
 * and historical adjustments associated with specific data objects.
 *
 * @param props - The component properties.
 * @returns A data-bound container displaying historical data updates.
 */
export const EntityHistoryLedgerCard = ({
  service,
  columns,
  outerParams,
  refreshTriggerRef,
  onManualRefresh
}: EntityHistoryLedgerCardProps) => {
  const { t } = useTranslation();

  return (
    <EnhancedCard
      title={t('global.history-update')}
      subtitle="Traccia gli eventi storici dello strumento. Modifica la data dei record o elimina le righe non corrette."
      buttons={
        <>
          {onManualRefresh && (
            <Tooltip title={t('actions.refresh-table')}>
              <IconButton onClick={onManualRefresh} color="primary" size="large">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      }>
      <EnhancedDataGrid
        columns={columns}
        service={service}
        outerParams={outerParams}
        refresh={refreshTriggerRef}
      />
    </EnhancedCard>
  );
};
